import type {
	AppState,
	NubeSDKRuntime,
	NubeSDKRuntimeState,
} from "@tiendanube/nube-sdk-internal-types";
import {
	getAppInitializer,
	serializeAppData,
	serializeInitialState,
} from "./code-handler";
import { addWorkerEventListener } from "./worker/event-listener";
import { createWorker } from "./worker/handler";
import { flushAllTelemetry, handleTelemetry } from "./worker/handler/telemetry";
import { isTelemetryEnabled } from "./worker/handler/telemetry/env";
import type { TelemetryLog } from "./worker/handler/telemetry/telemetry";

function sendAppRegisteredTelemetry(
	app: AppState,
	state: NubeSDKRuntimeState,
): void {
	if (!isTelemetryEnabled()) {
		return;
	}

	try {
		const sessionId = state.session?.id ?? "unknown";
		const storeId = state.store?.id ?? 0;
		const storeName = state.store?.name ?? "unknown";
		const storeDomain = state.store?.domain ?? "unknown";

		const telemetryLog: TelemetryLog = {
			type: "INFO",
			appId: app.id,
			scriptUrl: app.script,
			storeId,
			storeName,
			storeDomain,
			source: "app-registered",
			startTime: Date.now() * 1_000_000,
			message: `App ${app.id} registered`,
		};

		handleTelemetry(sessionId, {
			app: app.id,
			type: "telemetry:logs",
			data: telemetryLog,
		});
	} catch {
		// Telemetry failures should not break app registration
	}
}

const NUBE_DEVTOOLS_APPLICATION_SERVER_KEY = "nube-devtools-application-server";

type DevtoolsServerData = {
	script: string;
	connected: boolean;
	appId: string;
	type: "existing" | "new";
};

function isDevtoolsEnabled(): boolean {
	return window.__NUBE_DEVTOOLS_EXTENSION__ === true;
}

function mergeDevtoolsApp(state: NubeSDKRuntimeState): NubeSDKRuntimeState {
	let data: DevtoolsServerData | null = null;
	try {
		const raw = sessionStorage.getItem(NUBE_DEVTOOLS_APPLICATION_SERVER_KEY);
		if (raw) data = JSON.parse(raw) as DevtoolsServerData;
	} catch (error) {
		console.error("Failed to load devtools application data:", error);
	}
	if (!data?.connected || !data?.appId || !data?.script) return state;

	if (data.type === "existing") {
		const existingApp = state.apps?.[data.appId];
		if (!existingApp) return state;

		return {
			...state,
			apps: {
				...state.apps,
				[data.appId]: { ...existingApp, script: data.script },
			},
		};
	}

	return {
		...state,
		apps: {
			...state.apps,
			[data.appId]: { id: data.appId, script: data.script },
		},
	};
}

function register(
	runtime: NubeSDKRuntime,
	state: NubeSDKRuntimeState,
	initializer: string,
) {
	window.__NUBE_SDK_APPS__ = window.__NUBE_SDK_APPS__ || [];
	const { apps } = state;
	const registered: Record<string, AppState> = {};

	for (const appId in apps) {
		const app = apps[appId] as AppState;
		if (app && !app.registered) {
			const worker = createWorker(
				serializeAppData(app),
				serializeInitialState(state),
				initializer,
			);

			addWorkerEventListener(appId, worker, runtime);
			app.registered = true;
			registered[appId] = app;
			window.__NUBE_SDK_APPS__.push({ id: appId, worker });

			sendAppRegisteredTelemetry(app, state);
		}
	}

	if (Object.keys(registered).length > 0) {
		runtime.send("register", "app:registered", () => ({
			apps: registered,
		}));
	}
}

// Track if we've already set up the beforeunload listener
let beforeunloadListenerAdded = false;

export async function attach(nube = window.nubeSDK) {
	const initializer = await getAppInitializer();
	const rawState = nube.getState();
	const state = isDevtoolsEnabled() ? mergeDevtoolsApp(rawState) : rawState;
	const hasApps = state?.apps && Object.keys(state.apps).length > 0;

	if (hasApps) register(nube, state, initializer);

	nube.on("app:register", (payload) => {
		const nextState = isDevtoolsEnabled() ? mergeDevtoolsApp(payload) : payload;
		register(nube, nextState, initializer);
	});

	// Set up a listener to flush telemetry before the page unloads
	// This ensures any buffered telemetry data is sent before the user navigates away
	// Only add the listener if telemetry is enabled
	if (!beforeunloadListenerAdded && isTelemetryEnabled()) {
		window.addEventListener("beforeunload", () => {
			flushAllTelemetry();
		});
		beforeunloadListenerAdded = true;
	}
}
